import { useEffect, useRef } from "react";

type CompareKeyboardHandlers = {
  /** J steps to the next change, K to the previous one. */
  onStep: (by: 1 | -1) => void;
  /** R marks the change stepped to as reviewed (or clears the mark). */
  onToggleReviewed: () => void;
  /** Esc leaves the comparison. */
  onExit: () => void;
};

function isTypingTarget(target: EventTarget | null): boolean {
  // Stryker disable next-line ConditionalExpression: equivalent; a window or SVG target has no isContentEditable and no field tagName, so the checks below return false anyway
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/**
 * The comparison's keyboard shortcuts, live only while a comparison is open.
 * Keys typed into a field, or pressed with a modifier, are left alone.
 */
export function useCompareKeyboard(
  enabled: boolean,
  handlers: CompareKeyboardHandlers,
) {
  // why a ref: the handlers are rebuilt every render, and re-binding the
  // listener each time would drop a key pressed between two renders.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      const key = event.key.toLowerCase();
      if (key === "j") {
        handlersRef.current.onStep(1);
      } else if (key === "k") {
        handlersRef.current.onStep(-1);
      } else if (key === "r") {
        handlersRef.current.onToggleReviewed();
      } else if (event.key === "Escape") {
        handlersRef.current.onExit();
      } else {
        return;
      }
      event.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}
